import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "./i18nextinit";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import MusicEditorPage from "./pages/MusicEditorPage";
import SongSummaryPage from "./pages/SongSummaryPage";
import MusicPlayerPage from "./pages/MusicPlayerPage";
import TrackSongPage from "./pages/TrackSongPage";
import { ROUTE_CONSTANT } from "./routing/route-constants";
import useVisitorTracking from "./hooks/useVisitorTracking";
import StickyChatV2 from "./utils/StickyChatV2.tsx";
import { useGTM} from "./utils/helper.ts"
import { applyDomainMetaTags } from "./utils/visitor";

applyDomainMetaTags();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
      gcTime: 10 * 60_000,
    },
  },
});

function LegacyMusicPlayerRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={`${ROUTE_CONSTANT.MUSIC_PLAYER}${location.search}`}
      replace
    />
  );
}

function LegacyLocaleMusicPlayerRedirect() {
  const location = useLocation();
  const locale = location.pathname.split("/")[2];
  const target = ROUTE_CONSTANT.MUSIC_PLAYER_LOCALE.replace(":locale", locale);
  return <Navigate to={`${target}${location.search}`} replace />;
}

function LegacyLocaleOrderSummaryRedirect() {
  const location = useLocation();
  const locale = location.pathname.split("/")[2];
  const target = ROUTE_CONSTANT.TRACK_SONG_LOCALE.replace(":locale", locale);
  return <Navigate to={`${target}${location.search}`} replace />;
}

function LegacyLocaleTrackSongRedirect() {
  const location = useLocation();
  const locale = location.pathname.split("/")[2];
  const target = ROUTE_CONSTANT.TRACK_SONG_LOCALE.replace(":locale", locale);
  return <Navigate to={`${target}${location.search}`} replace />;
}

function LegacyOrderSummaryRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={`${ROUTE_CONSTANT.TRACK_SONG}${location.search}`}
      replace
    />
  );
}

function LegacyTrackSongRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={`${ROUTE_CONSTANT.TRACK_SONG}${location.search}`}
      replace
    />
  );
}

function LegacySongSummaryRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={`${ROUTE_CONSTANT.SONG_SUMMARY}${location.search}`}
      replace
    />
  );
}

function LegacyLocaleSongSummaryRedirect() {
  const location = useLocation();
  const locale = location.pathname.split("/")[2];
  const target = ROUTE_CONSTANT.SONG_SUMMARY_LOCALE.replace(":locale", locale);
  return <Navigate to={`${target}${location.search}`} replace />;
}

function LegacyAltSongSummaryRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={`${ROUTE_CONSTANT.SONG_SUMMARY}${location.search}`}
      replace
    />
  );
}

function AppBootstrap() {
  useVisitorTracking();
  useGTM();

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/app/:locale/music-editor"
          element={<MusicEditorPage />}
        />
        <Route path="/app/music-editor" element={<MusicEditorPage />} />
        <Route
          path={ROUTE_CONSTANT.SONG_SUMMARY_LOCALE}
          element={<SongSummaryPage />}
        />
        <Route
          path={ROUTE_CONSTANT.SONG_SUMMARY}
          element={<SongSummaryPage />}
        />
        <Route
          path={ROUTE_CONSTANT.SONG_SUMMARY_LEGACY_LOCALE}
          element={<LegacyLocaleSongSummaryRedirect />}
        />
        <Route
          path={ROUTE_CONSTANT.SONG_SUMMARY_LEGACY}
          element={<LegacySongSummaryRedirect />}
        />
        <Route
          path={ROUTE_CONSTANT.SONG_SUMMARY_ALT}
          element={<LegacyAltSongSummaryRedirect />}
        />
        <Route
          path={ROUTE_CONSTANT.MUSIC_PLAYER_LOCALE}
          element={<MusicPlayerPage />}
        />
        <Route path={ROUTE_CONSTANT.MUSIC_PLAYER} element={<MusicPlayerPage />} />
        <Route
          path="/app/:locale/music-editor/music-player"
          element={<LegacyLocaleMusicPlayerRedirect />}
        />
        <Route
          path="/app/music-editor/music-player"
          element={<LegacyMusicPlayerRedirect />}
        />
        <Route
          path="/app/:locale/music-player"
          element={<LegacyLocaleMusicPlayerRedirect />}
        />
        <Route path="/app/music-player" element={<LegacyMusicPlayerRedirect />} />
        <Route
          path={ROUTE_CONSTANT.TRACK_SONG_LOCALE}
          element={<TrackSongPage />}
        />
        <Route
          path={ROUTE_CONSTANT.TRACK_SONG}
          element={<TrackSongPage />}
        />
        <Route
          path={ROUTE_CONSTANT.ORDER_SUMMARY_LEGACY_LOCALE}
          element={<LegacyLocaleOrderSummaryRedirect />}
        />
        <Route
          path={ROUTE_CONSTANT.ORDER_SUMMARY_LEGACY}
          element={<LegacyOrderSummaryRedirect />}
        />
        <Route
          path={ROUTE_CONSTANT.ORDER_SUMMARY_LEGACY_ALT_LOCALE}
          element={<LegacyLocaleOrderSummaryRedirect />}
        />
        <Route
          path={ROUTE_CONSTANT.ORDER_SUMMARY_LEGACY_ALT}
          element={<LegacyOrderSummaryRedirect />}
        />
        <Route
          path={ROUTE_CONSTANT.TRACK_SONG_LEGACY_LOCALE}
          element={<LegacyLocaleTrackSongRedirect />}
        />
        <Route
          path={ROUTE_CONSTANT.TRACK_SONG_LEGACY}
          element={<LegacyTrackSongRedirect />}
        />
        <Route
          path="*"
          element={<Navigate to="/app/music-editor?onboarding=true" replace />}
        />
      </Routes>
    </BrowserRouter>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <StickyChatV2/>
      <AppBootstrap />
    </QueryClientProvider>
  </StrictMode>,
);
